import { Popover } from '@mui/material';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import ChevronIcon from '../../../assets/svg/ChevronIcon';
import {
  AVAILABLE_REPORT_STATUSES_BY_REPORT_STATUS,
  STATUS_BG_COLOR_MAP,
} from '../../../constants';
import { TableBodyCell } from './TableBodyCell';

const getNextReports = ({
  reports,
  filterByStatuses,
  leadID,
  apptID,
  nextSelectedStatus,
}) => {
  const hasFilterByStatuses = filterByStatuses.length > 0;

  return reports
    .map(v => {
      if (
        (hasFilterByStatuses &&
          v.LeadID === leadID &&
          v.ApptID === apptID &&
          filterByStatuses.includes(nextSelectedStatus)) ||
        (!hasFilterByStatuses && v.LeadID === leadID && v.ApptID === apptID)
      ) {
        return { ...v, Status: nextSelectedStatus };
      }

      if (
        (!hasFilterByStatuses && v.LeadID !== leadID && v.ApptID !== apptID) ||
        (hasFilterByStatuses &&
          v.LeadID !== leadID &&
          v.ApptID !== apptID &&
          filterByStatuses.includes(v.Status))
      ) {
        return v;
      }

      return null;
    })
    .filter(Boolean);
};

export const TableCellStatus = ({
  status,
  leadID,
  apptID,
  onChangeReports,
  filterByStatuses,
  ...props
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(status);

  const onChangeSelectedStatus = async nextSelectedStatus => {
    setSelectedStatus(nextSelectedStatus);
    onClosePopover();
    setIsSubmitting(true);

    try {
      const response = await fetch('/update-booking-status', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          LeadID: leadID,
          ApptID: apptID,
          bookingStatus: nextSelectedStatus,
        }),
      });

      if (response.status !== 200) throw new Error();

      onChangeReports(reports =>
        getNextReports({
          reports,
          filterByStatuses,
          leadID,
          apptID,
          nextSelectedStatus,
        }),
      );
      setIsSubmitting(false);
    } catch {
      setIsSubmitting(false);
      setSelectedStatus(status);
      toast.error('Something went wrong, please try again!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const onOpenPopover = e => {
    setAnchorEl(e.currentTarget);
  };

  const onClosePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const options = AVAILABLE_REPORT_STATUSES_BY_REPORT_STATUS[selectedStatus];

  return (
    <TableBodyCell {...props}>
      <button
        className={twMerge(
          'h-8 flex items-center gap-1 rounded-lg py-0.5 pr-2 pl-3 focus-visible:outline-none',
          STATUS_BG_COLOR_MAP[selectedStatus],
        )}
        onClick={onOpenPopover}
        disabled={isSubmitting}
      >
        <span className="text-base-1000 font-medium text-[14px]/[20px]">
          {selectedStatus}
        </span>
        <ChevronIcon className="w-4 h-4 text-base-1000" />
      </button>
      <Popover
        open={open}
        slotProps={{
          paper: {
            className:
              '!rounded-xl mt-1 !shadow-[0_4px_20px_0_rgba(17,17,58,0.08)] bg-white w-40',
          },
        }}
        anchorEl={anchorEl}
        onClose={onClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div className="p-0.5 flex flex-col gap-0.5">
          {options.map(({ label, value, icon }) => (
            <div
              key={value}
              className="flex items-center gap-2 py-1.5 px-2 cursor-pointer"
              onClick={() => {
                onChangeSelectedStatus(value);
              }}
            >
              {icon}
              <span className="font-medium text-[14px]/[20px] text-base-1000">
                {label}
              </span>
            </div>
          ))}
        </div>
      </Popover>
    </TableBodyCell>
  );
};
